import React, { memo } from 'react';
import { Button, Dropdown, Tag } from 'antd';
import {
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  LinkOutlined,
  MoreOutlined,
  PictureOutlined,
  TableOutlined,
} from '@ant-design/icons';

const PERMISSION_COLOR_MAP = {
  only_me: 'red',
  all_team_members: 'blue',
  public: 'green',
};

const PERMISSION_TEXT_MAP = {
  only_me: '仅我',
  all_team_members: '团队成员',
  public: '公开',
};

const TYPE_ICON_MAP = {
  doc: { Icon: FileTextOutlined, className: 'kb-manage-card__icon--doc' },
  xls: { Icon: TableOutlined, className: 'kb-manage-card__icon--xls' },
};

const formatWordCount = (count) => {
  const value = Number(count) || 0;
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return value.toLocaleString();
};

const getDescription = (record) => {
  const text = (record.description || '').trim();
  if (!text) return '暂无描述';
  return text.length > 72 ? `${text.slice(0, 72)}…` : text;
};

const KnowledgeManageCard = memo(({
  record,
  canEdit,
  canDelete,
  onViewDetail,
  onAccess,
  onUploadImage,
  onDelete,
}) => {
  const typeConfig = TYPE_ICON_MAP[record.type] || {
    Icon: DatabaseOutlined,
    className: 'kb-manage-card__icon--default',
  };
  const TypeIcon = typeConfig.Icon;
  const permission = record.permission || 'only_me';
  const createdLabel = record.created_at
    ? new Date(record.created_at * 1000).toLocaleDateString('zh-CN')
    : '-';

  const menuItems = [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: '查看详情',
      onClick: () => onViewDetail(record),
    },
    {
      key: 'access',
      icon: <LinkOutlined />,
      label: '查看文档',
      onClick: () => onAccess(record),
    },
    {
      key: 'upload_image',
      icon: <PictureOutlined />,
      label: '上传图片到知识库',
      onClick: () => onUploadImage(record),
    },
  ];

  if (canEdit) {
    menuItems.push({
      key: 'edit',
      icon: <EditOutlined />,
      label: '编辑',
      disabled: true,
      onClick: () => {},
    });
  }

  if (canDelete) {
    menuItems.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      label: '删除',
      danger: true,
      onClick: () => onDelete(record),
    });
  }

  return (
    <article className="kb-manage-card">
      <div className="kb-manage-card__head">
        <div className={`kb-manage-card__icon ${typeConfig.className}`}>
          <TypeIcon />
        </div>
        <div className="kb-manage-card__main">
          <div className="kb-manage-card__name-row">
            <span
              className="kb-manage-card__name"
              title={record.name}
              onClick={() => onAccess(record)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onAccess(record);
                }
              }}
            >
              {record.name}
            </span>
            <Tag color={PERMISSION_COLOR_MAP[permission]} style={{ margin: 0, fontSize: 11 }}>
              {PERMISSION_TEXT_MAP[permission] || permission}
            </Tag>
          </div>
          <div className="kb-manage-card__desc">{getDescription(record)}</div>
        </div>
      </div>

      <div className="kb-manage-card__stats">
        <div className="kb-manage-card__stat">
          <div className="kb-manage-card__stat-value">{record.document_count ?? 0}</div>
          <div className="kb-manage-card__stat-label">文档</div>
        </div>
        <div className="kb-manage-card__stat">
          <div className="kb-manage-card__stat-value">{formatWordCount(record.word_count)}</div>
          <div className="kb-manage-card__stat-label">词数</div>
        </div>
        <div className="kb-manage-card__stat">
          <div className="kb-manage-card__stat-value">{record.app_count ?? 0}</div>
          <div className="kb-manage-card__stat-label">应用</div>
        </div>
      </div>

      <div className="kb-manage-card__meta">
        <span className="kb-manage-card__meta-item">创建于 {createdLabel}</span>
        {record.data_source_type && (
          <span className="kb-manage-card__meta-item">{record.data_source_type}</span>
        )}
      </div>

      <div className="kb-manage-card__actions">
        <Button type="text" size="small" icon={<LinkOutlined />} onClick={() => onAccess(record)}>
          文档
        </Button>
        <Button type="text" size="small" icon={<PictureOutlined />} onClick={() => onUploadImage(record)}>
          图片
        </Button>
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <Button type="text" size="small" icon={<MoreOutlined />} aria-label="更多操作" />
        </Dropdown>
      </div>
    </article>
  );
});

KnowledgeManageCard.displayName = 'KnowledgeManageCard';

export { PERMISSION_COLOR_MAP, PERMISSION_TEXT_MAP };
export default KnowledgeManageCard;
